import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "cursor-pointer focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-transparent bg-clip-padding text-sm font-medium focus-visible:ring-3 aria-invalid:ring-3 [&_svg:not([class*='size-'])]:size-4 group group/button inline-flex shrink-0 items-center justify-center whitespace-nowrap transition-all outline-none select-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        response:
          'w-full min-w-0 shrink justify-start whitespace-normal rounded-1.5xl border border-gray-200 bg-white text-left text-base font-medium text-indigo-900 shadow-[0px_1.758px_7.031px_0px_rgba(0,0,0,0.06)] transition-colors hover:bg-blue-50 [&[data-selected=true]]:border-blue-200 [&[data-selected=true]]:bg-blue-200 [&[data-selected=true]]:font-semibold',
        ctaPrimary:
          'w-full bg-indigo-700 text-white font-medium rounded-md hover:bg-indigo-800 active:bg-indigo-900 disabled:bg-indigo-700/50 disabled:opacity-100',
        ctaSecondary:
          'w-full bg-transparent text-gray-700 border border-gray-300 rounded-md text-base font-medium hover:bg-gray-50 active:bg-gray-200 focus:bg-white focus:border-gray-600',
        noBorder:
          'bg-transparent font-medium border-none rounded-md hover:bg-indigo-50 active:bg-indigo-100',
        offboarding:
          'w-full bg-white border border-gray-100 font-medium rounded-lg shadow-[0px_1.7px_7px_0px_rgba(8,15,54,0.05)] hover:bg-gray-50 active:bg-gray-100',
        icon: 'border-none',
        toggleSegment:
          'flex-1 border border-transparent bg-transparent text-sm font-medium shadow-none hover:bg-gray-50/50 [&[data-selected=true]]:bg-blue-50 [&[data-selected=true]]:border [&[data-selected=true]]:border-indigo-800 [&[data-selected=true]]:font-semibold',
        autocomplete:
          'w-full border border-gray-200 bg-white text-sm rounded-1.5xl focus:ring-0 focus:bg-gray-50 hover:bg-gray-50 text-left justify-between shadow-[0px_1.758px_7.031px_0px_rgba(0,0,0,0.06)] transition-colors hover:bg-blue-50 whitespace-normal',
        ghost:
          'border-none bg-transparent hover:bg-gray-100 active:bg-gray-200',
        outline:
          'border border-gray-200 bg-white hover:bg-gray-50 active:bg-gray-100',
      },
      size: {
        xs: 'min-h-12 w-40 nsm:w-48 gap-3 px-4 py-2',
        sm: 'min-h-10.5 w-72 gap-3 px-4 py-2',
        md: 'min-h-12 gap-3 px-4 py-2',
        lg: 'min-h-13 w-82.5 gap-3 px-4 py-2',
        xl: 'min-h-13 max-w-101 gap-3 px-4 py-2',
        offboarding:
          'min-h-12 w-full gap-3 px-4 py-2.5 [&_svg:not([class*="size-"])]:size-6',
        icon: 'size-8 [&_svg:not([class*="size-"])]:size-4',
        'icon-xs':
          'size-6 rounded-[min(var(--radius-md),10px)] [&_svg:not([class*="size-"])]:size-3',
        'icon-sm':
          'size-8 rounded-[min(var(--radius-md),12px)] [&_svg:not([class*="size-"])]:size-4',
        'icon-lg': 'size-12 [&_svg:not([class*="size-"])]:size-5',
        autocomplete:
          'h-12 pl-4 py-3.5 pr-3 w-full gap-2 [&_svg]:!size-7 [&_svg]:shrink-0 leading-normal',
      },
    },
    compoundVariants: [
      // ctaPrimary / ctaSecondary: size sm for viewport below 23rem (n2xs), then requested size above
      {
        variant: ['ctaPrimary', 'ctaSecondary'],
        size: 'md',
        class: '!min-h-10.5 !w-72 min-[23rem]:!min-h-12 min-[23rem]:!w-full',
      },
      {
        variant: ['ctaPrimary', 'ctaSecondary'],
        size: 'lg',
        class: '!min-h-10.5 !w-72 min-[23rem]:!min-h-13 min-[23rem]:!w-82.5',
      },
      {
        variant: ['ctaPrimary', 'ctaSecondary'],
        size: 'xl',
        class: '!min-h-10.5 !w-72 min-[23rem]:!min-h-13 min-[23rem]:!max-w-101',
      },
    ],
    defaultVariants: {
      variant: 'response',
      size: 'md',
    },
  },
);

function Button({
  className,
  variant = 'response',
  size = 'md',
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
